import { BossMechanic, DamageType } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { affixedSlow } from "./affixes";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import { immuneToDamage, immuneToSlow } from "./immunity";
import type { LevelSim } from "./levelSim";
import type { PetHit } from "./petKit";
import { applyDamage } from "./systems/combat";

/**
 * What a Pets v3 ability does to a raider (docs/PETS.md "Pets v3"), through the statuses the sim already has wherever it
 * has one: damage (applyDamage: armour, affixes, immunities, the heavy cut never applies — no tower, no area), a slow
 * (the Frost hit's slot: the stronger kept; Frostbound halves it; slow immunity and the Colossus refuse it), a stun (the
 * stun slot: a boss takes `bossStunTicks`, Frostbound halves it, slow immunity does not stop it), and three of the pet's
 * own: a burn (apart from the towers' stacks: fire immunity refuses it, its damage is burn damage), a mark (extra
 * damage taken from every source and extra kill points) and a dazzle (it strikes no troop: troopRules.ts troopBlow).
 */
export function applyPetHit(sim: LevelSim, e: Enemy, hit: PetHit, damage: Fix64 = hit.damage): void {
  if (damage.isPositive()) applyDamage(sim, e, null, damage, hit.damageType);
  if (!e.alive) return;
  if (hit.burnTicks > 0 && !immuneToDamage(e, DamageType.Burn)) {
    if (hit.burnPerTick.gt(e.petBurnPerTick)) e.petBurnPerTick = hit.burnPerTick;
    e.petBurnTicks = Math.max(e.petBurnTicks, hit.burnTicks);
  }
  if (hit.slowTicks > 0 && !immuneToSlow(e) && sim.balance.enemies[e.archetype].bossMechanic !== BossMechanic.Colossus) {
    const slow = affixedSlow(sim, e, hit.slow);
    if (slow.gt(e.slowFraction)) e.slowFraction = slow;
    e.slowTicksLeft = Math.max(e.slowTicksLeft, hit.slowTicks);
  }
  if (hit.stunTicks > 0) {
    const t = affixedSlow(sim, e, Fix64.fromInt(e.isBoss && !e.isRare ? hit.bossStunTicks : hit.stunTicks)).floorToInt();
    if (t > e.stunTicksLeft) { e.stunTicksLeft = t; sim.events.add(SimEventKind.EnemyStunned, e.id, 0, t); }
  }
  if (hit.markTicks > 0) {
    const taken = Fix64.fromFraction(hit.markTakenPercent, PERCENT);
    if (taken.gt(e.markTaken)) e.markTaken = taken;
    e.markPointsPercent = Math.max(e.markPointsPercent, hit.markPointsPercent);
    e.markTicks = Math.max(e.markTicks, hit.markTicks);
  }
  if (hit.dazzleTicks > 0) e.dazzleTicks = Math.max(e.dazzleTicks, hit.dazzleTicks);
}

const PERCENT = 100;

/** Once a wave tick: the pet's burns deal their damage and run down; marks and dazzles run down. */
export function tickPetStatuses(sim: LevelSim): void {
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    if (e.markTicks > 0 && --e.markTicks === 0) { e.markTaken = Fix64.Zero; e.markPointsPercent = 0; }
    if (e.dazzleTicks > 0) e.dazzleTicks--;
    if (e.petBurnTicks <= 0) continue;
    const dmg = e.petBurnPerTick;
    if (--e.petBurnTicks === 0) e.petBurnPerTick = Fix64.Zero;
    applyDamage(sim, e, null, dmg, DamageType.Burn);
  }
}

/** Whether `e` is a ground raider the towers can target (or any such raider when `air`) within √r2 of (fx, fy). */
export function petCanReach(sim: LevelSim, e: Enemy, air: boolean, fx: Fix64, fy: Fix64, r2: Fix64): boolean {
  if (!e.alive || (e.isAir && !air) || !sim.targetable(e)) return false;
  const dx = e.x.sub(fx), dy = e.y.sub(fy);
  return dx.mul(dx).add(dy.mul(dy)).le(r2);
}

/** The raider within reach with the most health (ties to the lower id), or null. */
export function strongestInReach(sim: LevelSim, air: boolean, fx: Fix64, fy: Fix64, r2: Fix64): Enemy | null {
  let best: Enemy | null = null;
  for (const e of sim.enemySlots) {
    if (!petCanReach(sim, e, air, fx, fy, r2)) continue;
    if (best === null || e.hp.gt(best.hp) || (e.hp.eq(best.hp) && e.id < best.id)) best = e;
  }
  return best;
}
